import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  /**
   * Sequence number for refreshFromServer fetches. Bumped on every cart
   * update event so an in-flight refresh whose result is stale gets ignored.
   * @type {number}
   */
  #cartUpdateSeq = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * On bfcache restore, fetch the real cart state — sessionStorage may
   * be stale or missing if the user spent time on /cart before going back.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = () => {
    this.refreshFromServer();
  };

  /**
   * Fetches the real cart state from /cart.js and updates the bubble.
   */
  refreshFromServer = async ({ animate = false } = {}) => {
    const seqAtStart = this.#cartUpdateSeq;
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!res.ok) return;
      const cart = await res.json();
      // Drop the result if a newer cart update happened while we were fetching.
      if (seqAtStart !== this.#cartUpdateSeq) return;
      // Must match cart-bubble.liquid exactly, or the badge changes number by
      // itself after a cart update. Two rules there: "Add-on" products are
      // excluded, and a Custom PC Builder build — many line items sharing one
      // `_build` reference — counts as a single item rather than as its parts.
      const seenBuilds = new Set();
      const itemCount = (cart.items ?? []).reduce((sum, item) => {
        if (item.product_type === 'Add-on') return sum;
        const buildRef = item.properties && item.properties._build;
        if (buildRef) {
          if (seenBuilds.has(buildRef)) return sum;
          seenBuilds.add(buildRef);
          return sum + 1;
        }
        return sum + (item.quantity || 0);
      }, 0);
      this.renderCartBubble(itemCount, false, animate);
    } catch (_) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async () => {
    // Re-fetch the cart so the count matches the drawer (which also
    // excludes Add-on products). Avoids drift when the event's itemCount
    // delta includes add-ons that the server-rendered bubble ignores.
    this.#cartUpdateSeq++;
    this.refreshFromServer({ animate: true });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
